import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './PatientTypeChooserModal.css';

/**
 * A lightweight chooser modal that asks whether the patient is
 * an existing user or a new one before proceeding to visit creation.
 *
 * Props:
 *   isOpen           - boolean
 *   onClose          - () => void
 *   visitType        - 'inpatient' | 'outpatient'
 *   onExistingPatient - () => void  — user chose "Existing"
 *   onNewPatient      - () => void  — user chose "New"
 */
const PatientTypeChooserModal = ({
  isOpen,
  onClose,
  visitType = 'inpatient',
  onExistingPatient,
  onNewPatient,
}) => {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  const handleExisting = () => {
    onClose();
    if (onExistingPatient) onExistingPatient();
  };

  const handleNew = () => {
    onClose();
    if (onNewPatient) onNewPatient();
  };

  return createPortal(
    <div
      className="patient-chooser-overlay"
      onClick={onClose}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <div
        className="patient-chooser-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="patient-chooser-body">
          <div
            className="patient-chooser-card card-existing"
            onClick={handleExisting}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleExisting()}
          >
            <span className="patient-chooser-icon" aria-hidden="true">🔍</span>
            <div className="patient-chooser-card-title">
              {t('patientTypeChooser.existing.title', 'Existing Patient')}
            </div>
            <div className="patient-chooser-card-desc">
              {t('patientTypeChooser.existing.description', 'Search from registered pet guardians')}
            </div>
          </div>

          <div
            className="patient-chooser-card card-new"
            onClick={handleNew}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleNew()}
          >
            <span className="patient-chooser-icon" aria-hidden="true">➕</span>
            <div className="patient-chooser-card-title">
              {t('patientTypeChooser.new.title', 'New Patient')}
            </div>
            <div className="patient-chooser-card-desc">
              {t('patientTypeChooser.new.description', 'Register a new pet guardian and their pet')}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PatientTypeChooserModal;
